import type { Node } from '@xyflow/react';
import { computePinsForNodeData, pruneInvalidEdgesForNode, shouldSchemaDrivePins } from '@features/flows/lib/pins';
import { getTemplateForNode } from '@features/flows/lib/autocast/edge-context';
import { ensureBinding } from '@features/flows/lib/bindings';
import { deepMerge } from '@features/flows/utils/deep-merge';
import type { NodeBindings } from '@features/flows/utils/types';
import type { CanvasState, SliceCreator } from '../types';
import {
  cryptoRandom,
  nextCommandInsertSerial,
  pickBestInPin,
  pickBestOutPin,
  pushHistory,
} from '../utils';
import type { SliceCtx } from '../ctx';

type SliceKeys =
  | 'setNodeAttrs'
  | 'toggleAttrPin'
  | 'setBindingSpec'
  | 'recomputePins'
  | 'setNodeLabel'
  | 'insertNodeFromCommand'
  | 'insertNodeFromTemplate'
  | 'removeNodeById'
  | 'patchNodeAttrs';

export const createNodesSlice: SliceCreator<Pick<CanvasState, SliceKeys>> = (set, get) => {
  const resolveCtx = (): SliceCtx | undefined => (get as any).__ctx;
  const ensureCtx = () => {
    const ctx = resolveCtx();
    if (!ctx) throw new Error('canvas ctx missing for nodes slice');
    return ctx;
  };

  return {
    setNodeAttrs: (nodeId, next) => {
      const { queueInference, bumpStructureVersion } = ensureCtx();
      set((state) => {
        const idx = state.graph.nodes.findIndex((node) => node.id === nodeId);
        if (idx < 0) return;
        pushHistory(state);
        const node = state.graph.nodes[idx]!;
        (node.data as any).attrs = next;
        const tpl = getTemplateForNode(node, get().templates);
        const schemaDriven = shouldSchemaDrivePins(node.data);
        (node.data as any).pins = computePinsForNodeData(node.data, {
          preserveExistingDataIn: true,
          attrSchema: (tpl as any)?.attrsSchema,
          baseDataIn: (tpl as any)?.ports?.data?.in,
          baseDataOut: (tpl as any)?.ports?.data?.out,
          preserveExistingDataOut: !schemaDriven,
        });
        if ((node.data as any)?.type === 'end') {
          if ((node.data as any)?.pins?.data) delete (node.data as any).pins.data;
        }
        const prevEdgesLength = state.graph.edges.length;
        const pruned = pruneInvalidEdgesForNode(nodeId, state.graph.nodes, state.graph.edges);
        state.graph.edges = pruned.edges;
        if (pruned.edges.length !== prevEdgesLength) {
          bumpStructureVersion(state);
        }
      });
      queueInference({ immediate: true });
    },

    toggleAttrPin: (nodeId, field) => {
      const { queueInference, bumpStructureVersion } = ensureCtx();
      set((state) => {
        const idx = state.graph.nodes.findIndex((node) => node.id === nodeId);
        if (idx < 0) return;
        pushHistory(state);
        const node = state.graph.nodes[idx]!;
        const bindings: NodeBindings = { ...(node.data as any).bindings };
        const curr = ensureBinding(bindings?.[field], field);
        if (curr.exposable === false) {
          return;
        }
        const pinned = !!curr.pin && curr.precedence === 'pinOnly';
        if (pinned) {
          curr.pin = undefined;
          curr.precedence = 'pin>attr';
        } else {
          curr.pin = field;
          curr.precedence = 'pinOnly';
        }
        (node.data as any).bindings = { ...bindings, [field]: curr };
        const tpl = getTemplateForNode(node, get().templates);
        const schemaDriven = shouldSchemaDrivePins(node.data);
        (node.data as any).pins = computePinsForNodeData(node.data, {
          preserveExistingDataIn: true,
          attrSchema: (tpl as any)?.attrsSchema,
          baseDataIn: (tpl as any)?.ports?.data?.in,
          baseDataOut: (tpl as any)?.ports?.data?.out,
          preserveExistingDataOut: !schemaDriven,
        });
        if ((node.data as any)?.type === 'end') {
          if ((node.data as any)?.pins?.data) delete (node.data as any).pins.data;
        }
        const prevEdgesLength = state.graph.edges.length;
        const pruned = pruneInvalidEdgesForNode(nodeId, state.graph.nodes, state.graph.edges);
        state.graph.edges = pruned.edges;
        if (pruned.edges.length !== prevEdgesLength) {
          bumpStructureVersion(state);
        }
      });
      queueInference({ immediate: true });
    },

    setBindingSpec: (nodeId, field, partial) => {
      const { queueInference, bumpStructureVersion } = ensureCtx();
      set((state) => {
        const idx = state.graph.nodes.findIndex((node) => node.id === nodeId);
        if (idx < 0) return;
        pushHistory(state);
        const node = state.graph.nodes[idx]!;
        const bindings: NodeBindings = { ...(node.data as any).bindings };
        const merged = { ...ensureBinding(bindings?.[field], field), ...partial };
        (node.data as any).bindings = { ...bindings, [field]: merged };
        const tpl = getTemplateForNode(node, get().templates);
        const schemaDriven = shouldSchemaDrivePins(node.data);
        (node.data as any).pins = computePinsForNodeData(node.data, {
          preserveExistingDataIn: true,
          attrSchema: (tpl as any)?.attrsSchema,
          baseDataIn: (tpl as any)?.ports?.data?.in,
          baseDataOut: (tpl as any)?.ports?.data?.out,
          preserveExistingDataOut: !schemaDriven,
        });
        if ((node.data as any)?.type === 'end') {
          if ((node.data as any)?.pins?.data) delete (node.data as any).pins.data;
        }
        const prevEdgesLength = state.graph.edges.length;
        const pruned = pruneInvalidEdgesForNode(nodeId, state.graph.nodes, state.graph.edges);
        state.graph.edges = pruned.edges;
        if (pruned.edges.length !== prevEdgesLength) {
          bumpStructureVersion(state);
        }
      });
      queueInference({ immediate: true });
    },

    recomputePins: (nodeId) => {
      const { queueInference, bumpStructureVersion } = ensureCtx();
      set((state) => {
        const node = state.graph.nodes.find((n) => n.id === nodeId);
        if (!node) return;
        pushHistory(state);
        const tpl = getTemplateForNode(node, get().templates);
        const schemaDriven = shouldSchemaDrivePins(node.data);
        (node.data as any).pins = computePinsForNodeData(node.data, {
          preserveExistingDataIn: true,
          attrSchema: (tpl as any)?.attrsSchema,
          baseDataIn: (tpl as any)?.ports?.data?.in,
          baseDataOut: (tpl as any)?.ports?.data?.out,
          preserveExistingDataOut: !schemaDriven,
        });
        if ((node.data as any)?.type === 'end') {
          if ((node.data as any)?.pins?.data) delete (node.data as any).pins.data;
        }
        const prevEdgesLength = state.graph.edges.length;
        const pruned = pruneInvalidEdgesForNode(nodeId, state.graph.nodes, state.graph.edges);
        state.graph.edges = pruned.edges;
        if (pruned.edges.length !== prevEdgesLength) {
          bumpStructureVersion(state);
        }
      });
      queueInference({ immediate: true });
    },

    setNodeLabel: (nodeId, label) =>
      set((state) => {
        const idx = state.graph.nodes.findIndex((node) => node.id === nodeId);
        if (idx < 0) return;
        pushHistory(state);
        (state.graph.nodes[idx]!.data as any).label = label;
      }),

    insertNodeFromCommand: (raw) => {
      const { queueInference, bumpStructureVersion } = ensureCtx();
      set((state) => {
        const handler = raw.handler;
        if (!handler) return;
        const existing = raw.id ? state.graph.nodes.find((n) => n.id === raw.id) : undefined;
        if (existing) {
          console.warn('[Elora] insertNodeFromCommand skipped: node already exists', { nodeId: raw.id });
          return;
        }
        const tpl = state.templates?.get(handler);
        const nodeId = raw.id && raw.id.length ? raw.id : `${handler}:${cryptoRandom(6)}`;
        const serial = nextCommandInsertSerial();
        const offset = (serial % 7) * 24;
        const position = raw.position ?? { x: 120 + offset, y: 120 + offset };
        const execInPins = (tpl?.ports?.exec?.in ?? []).map((pp: any) => ({ id: pp.id }));
        const execOutPins = (tpl?.ports?.exec?.out ?? []).map((pp: any) => ({ id: pp.id }));
        const pins = {
          exec: {
            in: execInPins.length ? execInPins : [{ id: 'in' }],
            out: execOutPins.length ? execOutPins : [{ id: 'next' }],
          },
          data: {
            in: (tpl?.ports?.data?.in ?? []).map((pp: any) => ({
              id: pp.id,
              type: pp.type,
              typeExpr: (pp as any)?.typeExpr,
            })),
            out: (tpl?.ports?.data?.out ?? []).map((pp: any) => ({
              id: pp.id,
              type: pp.type,
              typeExpr: (pp as any)?.typeExpr,
            })),
          },
        } as any;
        const category = String(tpl?.category ?? '').toLowerCase();
        const pkg = String((tpl as any)?.pkg ?? '').toLowerCase();
        const handlerKey = String(handler);
        const looksPrimitive =
          handlerKey.startsWith('primitives.') ||
          pkg === 'primitives' ||
          category.includes('primitive');
        const isTrigger = category === 'trigger';
        const defaults = looksPrimitive ? { asConst: true } : undefined;
        const baseAttrs = defaults ? { ...defaults } : undefined;
        const incomingAttrs =
          raw.attrs && typeof raw.attrs === 'object' ? (raw.attrs as Record<string, any>) : undefined;
        const nextAttrs = incomingAttrs
          ? baseAttrs
            ? deepMerge(baseAttrs, incomingAttrs)
            : incomingAttrs
          : baseAttrs;
        const nodeData: any = {
          label: raw.label || tpl?.title || handlerKey,
          icon: tpl?.icon,
          pins,
          templateKey: handlerKey,
          handler: handlerKey,
          handlerVersion: (tpl as any)?.version ?? '1.0.0',
          persistedType: raw.persistedType || (tpl as any)?.nodeType,
          ...(isTrigger ? { isStart: true } : {}),
        };
        if (nextAttrs) nodeData.attrs = nextAttrs;
        pushHistory(state);
        state.graph.nodes = state.graph.nodes.concat({
          id: nodeId,
          type: 'default',
          position,
          data: nodeData,
        } as any);
        bumpStructureVersion(state);
        state.ui.selection = { nodes: new Set([nodeId]), edges: new Set() };
        state.ui.pendingCenterNodeId = nodeId;
      });
      queueInference({ immediate: true });
    },

    insertNodeFromTemplate: (tpl, pos) => {
      const { queueInference, bumpStructureVersion } = ensureCtx();
      set((state) => {
        const nodeId = `${tpl.key}:${cryptoRandom(6)}`;
        const pins = {
          exec: {
            in: (tpl.ports?.exec?.in ?? []).map((pp: any) => ({ id: pp.id })),
            out: (tpl.ports?.exec?.out ?? []).map((pp: any) => ({ id: pp.id })),
          },
          data: {
            in: (tpl.ports?.data?.in ?? []).map((pp: any) => ({
              id: pp.id,
              type: pp.type,
              typeExpr: (pp as any)?.typeExpr,
            })),
            out: (tpl.ports?.data?.out ?? []).map((pp: any) => ({
              id: pp.id,
              type: pp.type,
              typeExpr: (pp as any)?.typeExpr,
            })),
          },
        } as any;
        const isStart = String(tpl?.category || '').toLowerCase() === 'trigger';
        const looksPrimitive =
          String(tpl?.key || '').startsWith('primitives.') ||
          String((tpl as any)?.pkg || '').toLowerCase() === 'primitives' ||
          String(tpl?.category || '').toLowerCase().includes('primitive');
        const defaultAttrs: any = looksPrimitive ? { asConst: true } : undefined;
        const node: Node = {
          id: nodeId,
          type: 'default',
          position: pos,
          data: {
            label: tpl.title,
            icon: tpl.icon,
            pins,
            templateKey: tpl.key,
            handler: tpl.key,
            handlerVersion: (tpl as any)?.version ?? '1.0.0',
            persistedType: (tpl as any)?.nodeType,
            ...(defaultAttrs ? { attrs: defaultAttrs } : {}),
            ...(isStart ? { isStart: true } : {}),
          },
        } as any;
        pushHistory(state);
        state.graph.nodes = state.graph.nodes.concat(node);
        bumpStructureVersion(state);
        state.ui.selection = { nodes: new Set([nodeId]), edges: new Set() };
        const pending = state.ui.pendingConnect;
        if (pending) {
          if (pending.kind === 'exec') {
            if (pending.io === 'out') {
              const inPort = pins.exec.in?.[0]?.id;
              if (inPort) {
                state.graph.edges = state.graph.edges.concat({
                  id: `e-${pending.nodeId}-${nodeId}-${pending.handleId}-${inPort}`,
                  source: pending.nodeId,
                  target: nodeId,
                  sourceHandle: `exec:out:${pending.handleId}`,
                  targetHandle: `exec:in:${inPort}`,
                  data: { channel: 'exec', rawSourceHandle: pending.handleId, rawTargetHandle: inPort },
                } as any);
              }
            } else {
              const outPort = pins.exec.out?.[0]?.id;
              if (outPort) {
                state.graph.edges = state.graph.edges.concat({
                  id: `e-${nodeId}-${pending.nodeId}-${outPort}-${pending.handleId}`,
                  source: nodeId,
                  target: pending.nodeId,
                  sourceHandle: `exec:out:${outPort}`,
                  targetHandle: `exec:in:${pending.handleId}`,
                  data: { channel: 'exec', rawSourceHandle: outPort, rawTargetHandle: pending.handleId },
                } as any);
              }
            }
          } else if (pending.io === 'out') {
            const targets = pins.data.in ?? [];
            const expected = (pending as any).dataType ?? 'any';
            const chosen = pickBestInPin(targets, expected);
            if (chosen) {
              const id = `e-${pending.nodeId}-${nodeId}-${pending.handleId}-${chosen.id}`;
              state.graph.edges = state.graph.edges.concat({
                id,
                source: pending.nodeId,
                target: nodeId,
                sourceHandle: `data:out:${pending.handleId}`,
                targetHandle: `data:in:${chosen.id}`,
                data: { channel: 'data', rawSourceHandle: pending.handleId, rawTargetHandle: chosen.id },
              } as any);
            }
          } else {
            const sources = pins.data.out ?? [];
            const expected = (pending as any).dataType ?? 'any';
            const chosen = pickBestOutPin(sources, expected);
            if (chosen) {
              const id = `e-${nodeId}-${pending.nodeId}-${chosen.id}-${pending.handleId}`;
              state.graph.edges = state.graph.edges.concat({
                id,
                source: nodeId,
                target: pending.nodeId,
                sourceHandle: `data:out:${chosen.id}`,
                targetHandle: `data:in:${pending.handleId}`,
                data: { channel: 'data', rawSourceHandle: chosen.id, rawTargetHandle: pending.handleId },
              } as any);
            }
          }
          state.ui.pendingConnect = null;
        }
        state.ui.commandBar.open = false;
        state.ui.commandBar.query = '';
        state.ui.commandBar.context = null;
      });
      queueInference({ immediate: true });
    },

    removeNodeById: (nodeId) => {
      const { queueInference, bumpStructureVersion } = ensureCtx();
      set((state) => {
        if (!nodeId) return;
        const exists = state.graph.nodes.some((node) => node.id === nodeId);
        if (!exists) return;
        pushHistory(state);
        state.graph.nodes = state.graph.nodes.filter((node) => node.id !== nodeId);
        const removedEdgeIds = new Set<string>();
        state.graph.edges = state.graph.edges.filter((edge) => {
          const remove = edge.source === nodeId || edge.target === nodeId;
          if (remove) removedEdgeIds.add(edge.id);
          return !remove;
        });
        if (state.ui.selection.nodes.has(nodeId)) state.ui.selection.nodes.delete(nodeId);
        if (removedEdgeIds.size) {
          for (const id of removedEdgeIds) {
            if (state.ui.selection.edges.has(id)) state.ui.selection.edges.delete(id);
          }
        }
        bumpStructureVersion(state);
      });
      queueInference({ immediate: true });
    },

    patchNodeAttrs: (nodeId, patch) => {
      const { queueInference, bumpStructureVersion } = ensureCtx();
      set((state) => {
        if (!nodeId || !patch || typeof patch !== 'object') return;
        const idx = state.graph.nodes.findIndex((node) => node.id === nodeId);
        if (idx < 0) return;
        pushHistory(state);
        const node = state.graph.nodes[idx]!;
        const currentAttrs = ((node.data as any).attrs ?? {}) as Record<string, any>;
        const nextAttrs = deepMerge({ ...currentAttrs }, patch);
        (node.data as any).attrs = nextAttrs;
        const tpl = getTemplateForNode(node, get().templates);
        const schemaDriven = shouldSchemaDrivePins(node.data);
        (node.data as any).pins = computePinsForNodeData(node.data, {
          preserveExistingDataIn: true,
          attrSchema: (tpl as any)?.attrsSchema,
          baseDataIn: (tpl as any)?.ports?.data?.in,
          baseDataOut: (tpl as any)?.ports?.data?.out,
          preserveExistingDataOut: !schemaDriven,
        });
        if ((node.data as any)?.type === 'end') {
          if ((node.data as any)?.pins?.data) delete (node.data as any).pins.data;
        }
        const prevEdgesLength = state.graph.edges.length;
        const pruned = pruneInvalidEdgesForNode(nodeId, state.graph.nodes, state.graph.edges);
        const prunedIds = new Set(pruned.edges.map((edge) => edge.id));
        state.graph.edges = pruned.edges;
        if (pruned.edges.length !== prevEdgesLength) {
          bumpStructureVersion(state);
        }
        if (state.ui.selection.edges.size) {
          for (const id of Array.from(state.ui.selection.edges)) {
            if (!prunedIds.has(id)) state.ui.selection.edges.delete(id);
          }
        }
      });
      queueInference({ immediate: true });
    },
  };
};
